import React, { useCallback, useRef, useState } from "react";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  ComputedTableSource,
  getComputedTableSources,
  VersionLabel,
} from "@/lib/api/computedTables";
import { Button } from "@/components/common/Button";
import { StatusChip } from "@/components/common/StatusChip";
import { TooltipButton } from "@/components/common/TooltipButton";
import { Tooltip } from "@/components/common/Tooltip";
import { TruncatedText } from "@/components/common/TruncatedText";
import {
  HoverCardList,
  HoverCardNote,
  HoverCardSkeleton,
  HoverCardSurface,
} from "@/components/common/HoverCard";

const isNamed = (l: VersionLabel): boolean =>
  l.kind === "released" || l.kind === "checkpoint";

/// The build cut as the release chip used across the app: an accent chip for
/// both a release (rocket) and a checkpoint (flag), differentiated by icon +
/// tooltip. `not_retained` warns that the built-from version has since been
/// pruned; `unnamed` renders nothing (callers skip it).
const ReleaseChip: React.FC<{ label: VersionLabel }> = ({ label }) => {
  let title: string;
  let chip: React.ReactElement;
  switch (label.kind) {
    case "released":
      title = `Release “${label.name}”`;
      chip = (
        <StatusChip tone="accent" className="inline-flex items-center gap-0.5">
          <RocketLaunchOutlinedIcon className="icon-xs" />
          {label.name}
        </StatusChip>
      );
      break;
    case "checkpoint":
      title = `Checkpoint “${label.name}”`;
      chip = (
        <StatusChip tone="accent" className="inline-flex items-center gap-0.5">
          <FlagOutlinedIcon className="icon-xs" />
          {label.name}
        </StatusChip>
      );
      break;
    case "not_retained":
      title = "Original build version no longer kept";
      chip = <StatusChip tone="warning">version pruned</StatusChip>;
      break;
    case "unnamed":
      return null;
  }
  return (
    <Tooltip title={title} placement="top">
      {chip}
    </Tooltip>
  );
};

/// One source table a computed table was built from. Table icon + name, its
/// parent model as an underlined link that opens the model in a new tab zoomed
/// to the table (mirrors the Datasets-page table rows), and the build cut as a
/// release chip. A source that has moved on since the build is tagged "behind".
const SourceRow: React.FC<{ source: ComputedTableSource }> = ({ source }) => {
  if (source.status !== "ok") {
    const reason = source.status === "trashed" ? "Trashed" : "Access removed";
    return (
      <li className="flex items-center gap-2 px-2.5 py-1.5">
        <TableChartOutlinedIcon className="icon-sm text-fg-muted shrink-0" />
        <span className="flex-1 truncate text-fg-muted-regular">
          {source.source_name ?? "Source"}
        </span>
        <StatusChip tone="muted">{reason}</StatusChip>
      </li>
    );
  }

  // A pruned original (`not_retained`) still carries information, so it shows a
  // chip too — only a plain unnamed cut is silent.
  const showBuiltFrom = source.built_label.kind !== "unnamed";
  const showNowCut =
    source.is_behind &&
    source.current_label !== null &&
    isNamed(source.current_label);
  const canOpenModel =
    source.parent_model_id !== null && source.parent_model_name !== null;

  return (
    <li className="flex items-start gap-2 px-2.5 py-1.5">
      <TableChartOutlinedIcon className="icon-sm text-fg-muted shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <TruncatedText className="text-fg-regular">
          {source.source_name}
        </TruncatedText>
        <div className="truncate text-fg-muted-medium">
          {canOpenModel ? (
            <Tooltip
              title={`Open in ${source.parent_model_name}`}
              placement="top"
            >
              <a
                href={`/canvas/${source.parent_model_id}?table=${encodeURIComponent(source.source_table_id)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline hover:text-accent-hover"
              >
                {source.parent_model_name}
              </a>
            </Tooltip>
          ) : (
            "Shared directly"
          )}
        </div>
      </div>
      <div className="shrink-0 flex flex-col items-end gap-0.5">
        {showBuiltFrom && <ReleaseChip label={source.built_label} />}
        {source.is_behind &&
          (showNowCut && source.current_label ? (
            <span className="inline-flex items-center gap-0.5 text-fg-muted-medium">
              now <ReleaseChip label={source.current_label} />
            </span>
          ) : (
            <StatusChip tone="warning">behind</StatusChip>
          ))}
      </div>
    </li>
  );
};

interface ComputedTableSourcesMenuProps {
  computedTableId: string;
  tableName: string;
  /// Whether a source has advanced past this table's build version. Tints the
  /// trigger icon (outdated) and is the only condition under which Refresh
  /// shows — a fully-current table needs no refresh action.
  stale: boolean;
  /// Re-run the saved query against current source data (owner-only). Resolves
  /// when done; the card then re-fetches its rows.
  onRefresh: () => Promise<void>;
  refreshing: boolean;
  /// Fired as the hover card opens/closes so the parent row can keep its hover
  /// background while the pointer is inside the (portaled) card.
  onOpenChange?: (open: boolean) => void;
}

/// A computed table's "source versions" view: a trigger icon (tinted when
/// outdated) opening a hover card that lists each source table the computed
/// table was built from, the parent model it lives in, and the named build cut.
/// Refresh shows only when outdated. Rows load lazily on first open and re-fetch
/// stale-while-revalidate (kept mounted + dimmed during a refresh) so the card
/// never flickers.
export const ComputedTableSourcesMenu: React.FC<
  ComputedTableSourcesMenuProps
> = ({
  computedTableId,
  tableName,
  stale,
  onRefresh,
  refreshing,
  onOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const [sources, setSources] = useState<ComputedTableSource[] | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const requestedRef = useRef(false);
  const reqIdRef = useRef(0);

  const load = useCallback(() => {
    const id = ++reqIdRef.current;
    setPending(true);
    setError(false);
    getComputedTableSources(computedTableId)
      .then((s) => {
        if (reqIdRef.current === id) setSources(s);
      })
      .catch(() => {
        // Keep the last good rows on a revalidation failure; only surface the
        // error state when we have nothing to show.
        if (reqIdRef.current === id) setError(true);
      })
      .finally(() => {
        if (reqIdRef.current === id) setPending(false);
      });
  }, [computedTableId]);

  const ensureLoaded = useCallback(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    load();
  }, [load]);

  const handleRefresh = useCallback(async () => {
    try {
      await onRefresh();
    } finally {
      load();
    }
  }, [onRefresh, load]);

  const firstLoading = pending && sources === null;
  const firstError = error && sources === null;
  const hasRows = sources !== null && sources.length > 0;
  const isEmpty = sources !== null && sources.length === 0 && !pending;

  const card = (
    <HoverCardSurface maxWidth="24rem">
      <div className="px-2.5 pt-2 pb-1.5 border-b border-border">
        <div className="flex items-center gap-2">
          <p className="text-fg-bold grow">Source versions</p>
          {/* Only an outdated table has anything to refresh. */}
          {stale && (
            <TooltipButton
              title={
                refreshing ? "Refreshing…" : "Refresh from current versions"
              }
              variant="default"
              size="xs"
              aria-label={`Refresh ${tableName}`}
              disabled={refreshing || pending}
              onClick={() => void handleRefresh()}
            >
              <RefreshIcon />
            </TooltipButton>
          )}
        </div>
        <TruncatedText className="text-fg-regular">{tableName}</TruncatedText>
      </div>

      {firstLoading && <HoverCardSkeleton widths={[70, 55, 80, 50]} />}
      {firstError && (
        <HoverCardNote>Couldn’t load source versions.</HoverCardNote>
      )}
      {isEmpty && (
        <HoverCardNote>
          No source provenance recorded for this table.
        </HoverCardNote>
      )}
      {hasRows && (
        <div
          style={{
            opacity: pending ? 0.55 : 1,
            transition: "opacity var(--duration-short) var(--ease-move)",
          }}
        >
          <HoverCardList>
            {sources!.map((s) => (
              <SourceRow key={s.source_table_id} source={s} />
            ))}
          </HoverCardList>
        </div>
      )}
    </HoverCardSurface>
  );

  return (
    <Tooltip
      title={card}
      placement="top"
      variant="card"
      arrow
      instant
      interactive
      open={open}
      onOpen={() => {
        setOpen(true);
        onOpenChange?.(true);
        ensureLoaded();
      }}
      onClose={() => {
        setOpen(false);
        onOpenChange?.(false);
      }}
    >
      <Button
        variant="contextual"
        size="xs"
        aria-label={
          stale
            ? `Source versions for ${tableName} (outdated)`
            : `Source versions for ${tableName}`
        }
      >
        <AccountTreeOutlinedIcon
          style={stale ? { color: "var(--color-warning)" } : undefined}
        />
      </Button>
    </Tooltip>
  );
};
